/**
 * Карточка заявки — /requests/{id} (UC-G.07/08, canon design-guest §17).
 *
 * Шапка: название · категория · статус-бейдж. Трекер прогресса
 * (Создана → Назначена → В работе → Выполнена). Чат-лента комментариев
 * (мои справа, отеля слева) + поле отправки сообщения. Кнопка «Отменить
 * заявку» (только пока заявка не терминальна), с подтверждением.
 *
 * Гость видит ТОЛЬКО свою заявку — бэк отдаёт 404 на чужую (IDOR #329).
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Button, Card, Skeleton } from '@hsc/ui';
import { usePlatformMe } from '../../shared/api/usePlatformMe.js';
import { useAddComment, useCancelRequest, useRequestDetail } from './useRequests.js';
import { canCancel, statusMeta, TRACK_STEPS, trackIndex } from './status.js';

/** Время сообщения чата: «14:30». */
function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/** Трекер прогресса заявки (canon §17). */
function StatusTracker({ state }: { state: string }): JSX.Element {
  const idx = trackIndex(state);
  if (state === 'cancelled') {
    return (
      <div className="rq-track rq-track--cancelled" data-testid="request-track">
        <span className="rq-track__cancelled">Заявка отменена</span>
      </div>
    );
  }
  return (
    <ol className="rq-track" data-testid="request-track" aria-label="Прогресс заявки">
      {TRACK_STEPS.map((step, i) => {
        const cls =
          i < idx ? 'rq-step rq-step--done' : i === idx ? 'rq-step rq-step--active' : 'rq-step';
        return (
          <li key={step.state} className={cls} data-testid={`track-step-${step.state}`}>
            <span className="rq-step__dot" aria-hidden="true" />
            <span className="rq-step__label">{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

interface ChatValues {
  body: string;
}

export function RequestDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const { data, isLoading, isError, refetch } = useRequestDetail(id);
  const { data: me } = usePlatformMe();
  const myUserId = me?.actor.id ?? '';

  const addComment = useAddComment(id);
  const cancelMut = useCancelRequest(id);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [sendError, setSendError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChatValues>({ defaultValues: { body: '' } });

  if (isLoading) {
    return (
      <section aria-busy="true" data-testid="request-detail-skeleton">
        <Skeleton height={48} radius={14} />
        <div style={{ height: 12 }} />
        <Skeleton height={120} radius={14} />
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section data-testid="request-detail-error">
        <button type="button" className="ta-back" onClick={() => navigate('/requests')}>
          ‹ Мои заявки
        </button>
        <h1 className="hsc-page__title">Заявка</h1>
        <Card>
          <p role="alert">
            Не удалось открыть заявку. Возможно, её нет или она недоступна.
          </p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              Повторить
            </Button>
            <Button variant="ghost" onClick={() => navigate('/requests')}>
              К моим заявкам
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  const { request: rq, comments } = data;
  const meta = statusMeta(rq.state);

  const onSend = handleSubmit((values) => {
    const body = values.body.trim();
    if (body === '') return;
    setSendError('');
    addComment.mutate(
      { body },
      {
        onSuccess: () => reset({ body: '' }),
        onError: (err) =>
          setSendError(err.message !== '' ? err.message : 'Сообщение не отправилось'),
      },
    );
  });

  return (
    <section data-testid="request-detail-page">
      <button type="button" className="ta-back" onClick={() => navigate('/requests')}>
        ‹ Мои заявки
      </button>

      <div className="rq-detail-head">
        <h1 className="hsc-page__title">{rq.title}</h1>
        <span className={`rq-badge rq-badge--${meta.tone}`} data-testid="request-status-badge">
          {meta.label}
        </span>
      </div>
      <p className="ta-muted">
        {rq.category}
        {rq.priority === 'urgent' ? ' · Срочно' : ''}
      </p>
      {meta.hint !== '' && <p className="rq-detail-hint">{meta.hint}</p>}

      <StatusTracker state={rq.state} />

      {rq.items !== undefined && rq.items.length > 0 && (
        <Card>
          <h2 className="hsc-card__title">Состав заказа</h2>
          <ul className="rq-detail-items" data-testid="request-items">
            {rq.items.map((it, i) => (
              <li key={`${it.name}-${i}`} className="rq-detail-item">
                <span>
                  {it.name}
                  {it.variant !== undefined && it.variant !== '' ? ` · ${it.variant}` : ''} ×{' '}
                  {it.qty}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {rq.description !== undefined && rq.description !== '' && (
        <Card>
          <h2 className="hsc-card__title">Детали заявки</h2>
          <p className="rq-detail-desc">{rq.description}</p>
        </Card>
      )}

      {/* Чат-лента заявки (canon §17): сообщения гостя и отеля */}
      <section className="rq-chat" data-testid="request-chat" aria-label="Переписка по заявке">
        <h2 className="hsc-card__title">Переписка</h2>
        {comments.length === 0 ? (
          <p className="ta-muted">Пока сообщений нет — напишите, если что-то нужно уточнить.</p>
        ) : (
          <ul className="rq-msgs">
            {comments.map((c) => {
              const mine = myUserId !== '' && c.author_user_id === myUserId;
              return (
                <li
                  key={c.id}
                  className={mine ? 'rq-msg rq-msg--mine' : 'rq-msg rq-msg--them'}
                  data-testid={`request-msg-${c.id}`}
                >
                  <span className="rq-msg__body">{c.body}</span>
                  {/* #45 — фото, приложенные к заявке, показываем в деталях */}
                  {c.attachments !== undefined && c.attachments.length > 0 && (
                    <span className="rq-msg__photos" data-testid={`request-msg-${c.id}-photos`}>
                      {c.attachments
                        .filter((a) => a.url !== undefined && a.url !== '')
                        .map((a) => (
                          <a
                            key={a.key}
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="rq-msg__photo-link"
                          >
                            <img
                              className="rq-msg__photo"
                              src={a.url}
                              alt="Фото к заявке"
                              loading="lazy"
                            />
                          </a>
                        ))}
                    </span>
                  )}
                  <span className="rq-msg__time">{formatTime(c.created_at)}</span>
                </li>
              );
            })}
          </ul>
        )}

        {sendError !== '' && (
          <div className="ta-error-summary" role="alert" data-testid="chat-error">
            {sendError}
          </div>
        )}

        <form className="rq-chat-form" onSubmit={(e) => void onSend(e)} noValidate>
          <label className="hsc-input__label" htmlFor="rq-chat-body">
            Новое сообщение
          </label>
          <textarea
            id="rq-chat-body"
            className="ta-textarea"
            rows={2}
            placeholder="Напишите сообщение отелю…"
            data-testid="chat-input"
            {...register('body', { required: true, maxLength: 4000 })}
          />
          {errors.body && (
            <span className="ta-tooltip" role="alert">
              Введите текст сообщения
            </span>
          )}
          <div className="rq-actions">
            <Button type="submit" loading={addComment.isPending} data-testid="chat-send">
              Отправить
            </Button>
          </div>
        </form>
      </section>

      {/* Отмена заявки (UC-G.08) — только пока не терминальна */}
      {canCancel(rq.state) && (
        <Card>
          <h2 className="hsc-card__title">Передумали?</h2>
          {!confirmCancel ? (
            <Button
              variant="ghost"
              data-testid="cancel-request"
              onClick={() => setConfirmCancel(true)}
            >
              Отменить заявку
            </Button>
          ) : (
            <div data-testid="cancel-confirm">
              <p>Точно отменить заявку? Это действие нельзя вернуть.</p>
              {cancelMut.isError && (
                <div className="ta-error-summary" role="alert">
                  Не удалось отменить заявку. Попробуйте ещё раз.
                </div>
              )}
              <div className="rq-actions">
                <Button
                  variant="secondary"
                  loading={cancelMut.isPending}
                  data-testid="cancel-confirm-yes"
                  onClick={() => cancelMut.mutate(undefined)}
                >
                  Да, отменить
                </Button>
                <Button
                  variant="ghost"
                  data-testid="cancel-confirm-no"
                  onClick={() => setConfirmCancel(false)}
                >
                  Оставить
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </section>
  );
}
